import { useEffect, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { Platform } from '../../platform/platform.detect';
import { registerPushToken, addForegroundPushListener } from '../../platform/push.platform';
import { loadCapacitorApp } from '../../platform/capacitor.loader';
import { useNavigate } from 'react-router-dom';

// AIDEV-NOTE: Inicializar push nativo apos login bem-sucedido.
//             Em PWA, o usePushNotifications hook ja lida com VAPID.
//             Este componente e para push FCM/APNs em apps nativos.

interface PushMessage {
  title: string;
  body: string;
  route?: string;
}

export function NativePushInitializer() {
  const navigate = useNavigate();
  const [notification, setNotification] = useState<PushMessage | null>(null);

  const handleClose = useCallback(() => {
    setNotification(null);
  }, []);

  const handleClick = useCallback(() => {
    if (notification?.route) {
      navigate(notification.route);
    }
    setNotification(null);
  }, [notification, navigate]);

  useEffect(() => {
    if (!Platform.isNative()) return;

    // Registrar token FCM/APNs
    registerPushToken().catch((err) => {
      console.error('[NativePush] Erro ao registrar token:', err);
    });

    // Listener para notificacoes em foreground (app aberto)
    let cleanupFn: (() => void) | undefined;
    addForegroundPushListener((title, body, data) => {
      setNotification({
        title,
        body,
        route: data?.route
      });
    }).then((fn) => {
      cleanupFn = fn;
    });

    // Deep link handler — usuario clicou na notificacao com app fechado/background
    let deepLinkCleanup: (() => void) | undefined;
    loadCapacitorApp()
      .then((mod) => {
        const { App } = mod;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        App.addListener('appUrlOpen', (event: any) => {
          const url = new URL(event.url);
          const path = url.pathname + url.search;
          navigate(path);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).then((handle: any) => {
          deepLinkCleanup = () => handle.remove();
        });
      })
      .catch(() => {
        // @capacitor/app pode nao estar instalado
      });

    return () => {
      cleanupFn?.();
      deepLinkCleanup?.();
    };
  }, [navigate]);

  // AIDEV-NOTE: Exibe notificacao via MUI Snackbar (sem depender de notistack)
  return (
    <Snackbar
      open={!!notification}
      autoHideDuration={5000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClick={handleClick}
    >
      <Alert onClose={handleClose} severity="info" sx={{ cursor: 'pointer' }}>
        {notification ? `${notification.title}: ${notification.body}` : ''}
      </Alert>
    </Snackbar>
  );
}
